// NumerologyAcademyPage.js
import React, { useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faCircleCheck, faLock } from "@fortawesome/free-solid-svg-icons";
import DynamicLayout from "../../../components/DynamicLayout";
import ProgressBar from "../../components/ProgressBar";
import { AcademyContext } from "../../../context/academyContext";
import { useNavigate } from "react-router-dom";

const NumerologyAcademyPage = () => {
  const { progress } = useContext(AcademyContext);
  const [activeChapter, setActiveChapter] = useState(null);
  const [selectedMode, setSelectedMode] = useState("beginner");
  const navigate = useNavigate();

  const totalLessons = 40; // Adjust the total lesson count as needed

  const toggleChapter = (chapter) => {
    setActiveChapter(activeChapter === chapter ? null : chapter);
  };

  const renderIcon = (chapter, isAvailable) => (
    <FontAwesomeIcon
      icon={activeChapter === chapter ? faChevronUp : faChevronDown}
      className={isAvailable ? "has-text-warning" : "has-text-grey"}
    />
  );

  const renderCheckMark = (lessonNumber) => {
    return progress.completedLessons >= lessonNumber ? (
      <FontAwesomeIcon icon={faCircleCheck} className="has-text-warning ml-2" />
    ) : (
      <FontAwesomeIcon icon={faLock} className="has-text-grey ml-2" />
    );
  };

  const handleResumeClick = () => {
    let lessonNumber = progress.completedLessons + 1;
    const path = selectedMode === "beginner" ? "beginner" : "intermediate";
    navigate(`/academy/numerology/${path}/lesson?lesson=${lessonNumber}`);
  };

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
  };

  const renderLesson = (lessonNumber, lessonTitle) => {
    const isCompleted = progress.completedLessons >= lessonNumber;
    const path = selectedMode === "beginner" ? "beginner" : "intermediate";

    return (
      <li
        className={`${isCompleted ? "is-hoverable has-text-warning" : "has-text-grey"}`}
        style={{ cursor: isCompleted ? "pointer" : "default" }}
        onClick={() =>
          isCompleted &&
          navigate(`/academy/numerology/${path}/lesson?lesson=${lessonNumber}`)
        }
      >
        <span className="lesson-title">
          {lessonTitle} {renderCheckMark(lessonNumber)}
        </span>
      </li>
    );
  };

  const hasStartedCourse = progress.completedLessons > 0;
  const isHardModeUnlocked = progress.completedLessons >= totalLessons;

  return (
    <DynamicLayout>
      <div
        className="full-screen-image-container heightauto margin-top-0 has-text-warning"
        style={{
          backgroundImage: `url('/img/saturnwallpaper.jpg')`,
        }}
      >
        <section className="section hasbackgroundgrey heightauto has-text-centered has-text-warning">
          <h2 className="title has-text-centered has-text-warning">
            {hasStartedCourse ? "Welcome back to the Qabalistic Numerology Course." : "Welcome to the Qabalistic Numerology Course."}
          </h2>

          <ProgressBar completedLessons={progress.completedLessons} totalLessons={totalLessons} />
          
          <ul className="has-text-warning">
            {/* Chapter 1 */}
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.completedLessons >= 1 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter1")}
                style={{ cursor: "pointer" }}
              >
                Chapter 1: Introduction to Qabalistic Numerology {renderIcon("chapter1", progress.completedLessons >= 1)}
              </h2>
              <ul className={activeChapter === "chapter1" ? "" : "is-hidden"}>
                {renderLesson(1, "Lesson 1: The Tree of Life - Structure and Significance")}
                {renderLesson(2, "Lesson 2: Understanding Numerical Values in the Sephirot")}
                {renderLesson(3, "Lesson 3: The Paths of the Tree and Their Symbolism")}


              </ul>
            </li>

            {/* Chapter 2 */}
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.completedLessons >= 5 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter2")}
                style={{ cursor: "pointer" }}
              >
                Chapter 2: The Sephirot {renderIcon("chapter2", progress.completedLessons >= 5)}
              </h2>
              <ul className={activeChapter === "chapter2" ? "" : "is-hidden"}>
                {renderLesson(5, "Lesson 1: Kether (1) - The Crown")}
                {renderLesson(6, "Lesson 2: Chokmah (2) - Wisdom")}
                {renderLesson(7, "Lesson 3: Binah (3) - Understanding")}
                {/* Add more lessons as needed */}
              </ul>
            </li>

            {/* Chapter 3 */}
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.completedLessons >= 15 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter3")}
                style={{ cursor: "pointer" }}
              >
                Chapter 3: The Paths and Numbers {renderIcon("chapter3", progress.completedLessons >= 15)}
              </h2>
              <ul className={activeChapter === "chapter3" ? "" : "is-hidden"}>
                {renderLesson(15, "Path 11 - Aleph")}
                {renderLesson(16, "Path 12 - Beth")}
                {renderLesson(17, "Path 13 - Gimel")}
                {/* Add more lessons as needed */}
              </ul>
            </li>
          </ul>

          <button className="button is-warning mt-6" onClick={handleResumeClick}>
            {hasStartedCourse ? "Resume" : "Start"}
          </button>

          <div className="mt-6 buttons has-addons is-centered">
            <button
              className={`button is-small ${selectedMode === "beginner" ? "is-warning" : ""}`}
              onClick={() => handleModeChange("beginner")}
            >
              Beginner
            </button>
            <button
              className={`button is-small ${selectedMode === "intermediate" ? "is-warning" : ""} ${isHardModeUnlocked ? "" : "is-locked"}`}
              onClick={() => handleModeChange("intermediate")}
              disabled={!isHardModeUnlocked}
              title={isHardModeUnlocked ? "" : "Complete all lessons to unlock Intermediate mode"}
            >
              {!isHardModeUnlocked && <FontAwesomeIcon icon={faLock} className="lock-icon" />}
              Intermediate
            </button>
          </div>
        </section>
      </div>
    </DynamicLayout>
  );
};

export default NumerologyAcademyPage;
